import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import PhoneNumberField from "../../../../components/ui/PhoneNumberField";
import CustomTextField from "../../../../components/ui/Textfield";
import apiService from "../../../../service/Api";
import { RootState } from "../../../../store/Index";
import { tokens } from "../../../../theme";

export interface IReferralFormData {
  id: number;
  referral_name: string;
  referral_surname: string;
  email?: string;
  phone?: string;
}

interface AddReferralDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (referral: IReferralFormData) => void;
  initialValues?: Partial<FormState>;
}

// Form state matching component's fields
interface FormState {
  referral_name: string;
  referral_surname: string;
  email: string;
  phone: string;
}

// Field validation errors interface
interface FieldErrors {
  referral_name?: string;
  referral_surname?: string;
  email?: string;
  phone?: string;
}

const EMPTY: FormState = {
  referral_name: "",
  referral_surname: "",
  email: "",
  phone: "+263", // Preserved default code structure if needed
};

// Robust API error parsing utility brought over from your modal integration
function extractError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (typeof data === "object") {
    const detail = (data as { detail?: string }).detail;
    if (detail) return detail;
    return Object.entries(data as Record<string, unknown>)
      .map(([k, v]) =>
        Array.isArray(v) ? `${k}: ${v.join(", ")}` : `${k}: ${String(v)}`,
      )
      .join(" | ");
  }
  return fallback;
}

const AddReferralDialog: React.FC<AddReferralDialogProps> = ({
  open,
  onClose,
  onSaved,
  initialValues,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const isDarkMode = theme.palette.mode === "dark";
  const color400 = colors.primary[400] || "#724E41";
  const color10 = colors.primary[10] || "#C29363";

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>({ ...EMPTY, ...initialValues });
  const [errors, setErrors] = useState<FieldErrors>({});

  const set = (field: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, ...initialValues });
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const handleClose = () => {
    if (saving) return; // Prevent closing mid-flight
    setForm({ ...EMPTY });
    setErrors({});
    onClose();
  };

  // Upgraded validator providing granular user feedback matching the original design
  const validate = (): boolean => {
    const e: FieldErrors = {};
    if (!form.referral_name.trim()) e.referral_name = "Required";
    if (!form.referral_surname.trim()) e.referral_surname = "Required";

    if (!form.email.trim()) {
      e.email = "Required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      e.email = "Enter a valid email";
    }

    if (!form.phone.trim() || form.phone === "+263") {
      e.phone = "Required";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || saving) return;
    setSaving(true);
    try {
      // API call utilizing standard auth headers & sanitization rules
      const res = await apiService.post<IReferralFormData>(
        "/api/refferal-program/admin-create-partner/",
        {
          first_name: form.referral_name.trim(),
          last_name: form.referral_surname.trim(),
          email: form.email.trim(),
          phone_number: form.phone.replace(/\s+/g, "").trim(),
        },
      );

      toast.success(
        `Partner created! An invite email has been sent to ${form.email}.`,
        { position: "top-right", autoClose: 3000 },
      );

      onSaved(res.data);
      handleClose();
    } catch (error: unknown) {
      toast.error(extractError(error, "Failed to create partner."), {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>Add New Referral Partner</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <CustomTextField
            label="First Name"
            value={form.referral_name}
            onChange={(e) => set("referral_name", e.target.value)}
            required
            autoFocus
            error={!!errors.referral_name}
            helperText={errors.referral_name}
          />
          <CustomTextField
            label="Surname"
            value={form.referral_surname}
            onChange={(e) => set("referral_surname", e.target.value)}
            required
            error={!!errors.referral_surname}
            helperText={errors.referral_surname}
          />
          <CustomTextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            required
            error={!!errors.email}
            helperText={errors.email}
          />
          <PhoneNumberField
            label="Phone Number"
            variant="outlined"
            value={form.phone}
            onChange={(value) => set("phone", value || "")}
            error={!!errors.phone}
            helperText={errors.phone}
            placeholder="+2637XXXXXXXX"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          sx={{ color: isDarkMode ? colors.primary[200] : "#4A5568" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{
            background: `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`,
            color: "#fff",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            boxShadow: "none",
            "&:hover": { opacity: 0.9, boxShadow: "none" },
          }}
        >
          {saving ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Create & Send Invite"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddReferralDialog;
